import React from "react";
import { Table } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";

import type { Pagination } from "../types";

interface AntdDataTableProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  rowKey: string | ((record: T) => string);
  loading?: boolean;
  pagination?: Pagination;
  onPageChange?: (page: number, pageSize: number) => void;
  scrollX?: number | "max-content";
  showSrNo?: boolean;
  emptyText?: string;
}

export function AntdDataTable<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading = false,
  pagination,
  onPageChange,
  scrollX = "max-content",
  showSrNo = true,
  emptyText = "No records",
}: AntdDataTableProps<T>) {
  const finalColumns: ColumnsType<T> = showSrNo
    ? [
        {
          title: "SrNo.",
          dataIndex: "_sr_no",
          key: "_sr_no",
          width: 100,
          align: "left",
          render: (_: any, __: any, index: number) => {
            const current = pagination?.current_page ?? 1;
            const perPage =
              pagination?.per_page ?? (dataSource.length || 10);
            return (
              <span style={{ fontSize: 12, fontWeight: 500, color: "#6b7280" }}>
                {(current - 1) * perPage + index + 1}
              </span>
            );
          },
        },
        ...columns,
      ]
    : columns;

  const paginationConfig: TablePaginationConfig | false = pagination
    ? {
        current: pagination.current_page,
        pageSize: pagination.per_page,
        total: pagination.total,
        showSizeChanger: false,
        showTotal: (total: number, range: [number, number]) =>
          `Showing ${range[0]} - ${range[1]} of ${total} entries`,
        nextIcon: <i className="ti ti-chevron-right" />,
        prevIcon: <i className="ti ti-chevron-left" />,
        onChange: (page: number, pageSize: number) =>
          onPageChange?.(page, pageSize),
      }
    : false;

  return (
    <div className="slide-up-enter">
      <Table<T>
        dataSource={dataSource}
        columns={finalColumns}
        rowKey={rowKey as any}
        loading={loading}
        pagination={paginationConfig}
        scroll={{ x: scrollX }}
        size="middle"
        className="custom-data-table"
        style={{
          background: "#fff",
          borderRadius: "8px",
        }}
        rowClassName={(_, index) =>
          index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
        }
        locale={{ emptyText }}
      />
    </div>
  );
}

export default AntdDataTable;
